/**
 * Aplica a máscara de CPF ou CNPJ dinamicamente
 * CPF: 000.000.000-00 | CNPJ: XX.XXX.XXX/XXXX-XX
 */
export const maskCpfCnpj = (value: string): string => {
    if (!value) return "";

    let v = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    const hasLetters = /[A-Z]/.test(v);

    // Se passar de 11 caracteres ou contiver letras, vira CNPJ (14 posições)
    if (hasLetters || v.length > 11) {
        v = v.substring(0, 14);

        if (v.length > 12) {
            const base = v.substring(0, 12);
            const dv = v.substring(12, 14).replace(/[^0-9]/g, "");
            v = base + dv;
        }

        v = v.replace(/^([A-Z0-9]{2})([A-Z0-9])/, "$1.$2");
        v = v.replace(/^([A-Z0-9]{2})\.([A-Z0-9]{3})([A-Z0-9])/, "$1.$2.$3");
        v = v.replace(/\.([A-Z0-9]{3})([A-Z0-9])/, ".$1/$2");
        v = v.replace(/([A-Z0-9]{4})([0-9]{1,2})$/, "$1-$2");

        return v;
    }

    // Caso contrário, aplica máscara de CPF (até 11 posições numéricas)
    v = v.replace(/\D/g, "");
    v = v.substring(0, 11);

    v = v.replace(/(\d{3})(\d)/, "$1.$2");
    v = v.replace(/(\d{3})\.(\d{3})(\d)/, "$1.$2.$3");
    v = v.replace(/(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})$/, "$1.$2.$3-$4");

    return v;
};

/**
 * Valida estritamente o CPF
 */
const isValidCpf = (cpf: string): boolean => {
    const limpo = cpf.replace(/\D/g, "");
    if (limpo.length !== 11 || /^(\d)\1{10}$/.test(limpo)) return false;

    let soma = 0;
    for (let i = 0; i < 9; i++) soma += Number(limpo[i]) * (10 - i);
    let resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== Number(limpo[9])) return false;

    soma = 0;
    for (let i = 0; i < 10; i++) soma += Number(limpo[i]) * (11 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== Number(limpo[10])) return false;

    return true;
};

/**
 * Valida estritamente o CNPJ (Numérico ou Alfanumérico)
 */
const isValidCnpj = (cnpj: string): boolean => {
    const cnpjZerado = "00000000000000";
    const regexCaracteresNaoPermitidos = /[^A-Z\d./-]/i;
    const regexCNPJ = /^([A-Z\d]){12}(\d){2}$/;

    if (regexCaracteresNaoPermitidos.test(cnpj)) return false;

    const cnpjSemMascara = cnpj.replace(/[./-]/g, "");

    if (!regexCNPJ.test(cnpjSemMascara) || cnpjSemMascara === cnpjZerado) {
        return false;
    }

    const tamanhoCNPJSemDV = 12;
    const baseCnpj = cnpjSemMascara.substring(0, tamanhoCNPJSemDV);
    const dvInformado = cnpjSemMascara.substring(tamanhoCNPJSemDV);

    const calculaDV = (base: string): string => {
        const valorBase = "0".charCodeAt(0);
        const pesosDV = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

        let somatorioDV1 = 0;
        let somatorioDV2 = 0;

        for (let i = 0; i < tamanhoCNPJSemDV; i++) {
            const asciiVal = base.charCodeAt(i) - valorBase;
            somatorioDV1 += asciiVal * pesosDV[i + 1];
            somatorioDV2 += asciiVal * pesosDV[i];
        }

        const resto1 = somatorioDV1 % 11;
        const dv1 = resto1 < 2 ? 0 : 11 - resto1;

        somatorioDV2 += dv1 * pesosDV[tamanhoCNPJSemDV];
        const resto2 = somatorioDV2 % 11;
        const dv2 = resto2 < 2 ? 0 : 11 - resto2;

        return `${dv1}${dv2}`;
    };

    try {
        const dvCalculado = calculaDV(baseCnpj);
        return dvInformado === dvCalculado;
    } catch (e) {
        return false;
    }
};

/**
 * Valida CPF ou CNPJ dinamicamente
 */
export const isValidCpfCnpj = (doc: string): boolean => {
    if (!doc) return false;

    const cleaned = doc.replace(/[^a-zA-Z0-9]/g, "");

    if (/[A-Z]/i.test(cleaned) || cleaned.length === 14) {
        return isValidCnpj(doc);
    }

    if (cleaned.length === 11) {
        return isValidCpf(doc);
    }

    return false;
};